/**
 * Compact lead count display.
 * When count not fetched: shows emptyLabel (default "N/A").
 * When count available: shows "X left" (remaining = count - usage).
 * Hover shows total lead count.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { cn } from '@/lib/utils';

export interface LeadCountBadgeProps {
  /** Total leads in list (from SMSWiser catalog). Null/undefined = not fetched. */
  count?: number | null;
  /** Amount already used today (from prepared drops). When provided, shows remaining. */
  usage?: number;
  /** Remaining = count - usage. When provided (e.g. from backend), used directly. */
  remaining?: number | null;
  /** Compact style for tight layouts */
  compact?: boolean;
  /** Label when count not fetched. Default "N/A". */
  emptyLabel?: string;
  className?: string;
}

export const LeadCountBadge: FC<LeadCountBadgeProps> = ({
  count,
  usage,
  remaining: remainingProp,
  compact = false,
  emptyLabel = 'N/A',
  className,
}) => {
  if (count == null || (!Number.isFinite(count) && count !== 0)) {
    return (
      <span
        className={cn(
          compact ? 'text-[10px] italic' : 'text-xs italic',
          'text-muted-foreground',
          className,
        )}
      >
        {emptyLabel}
      </span>
    );
  }

  const total = Number(count);
  const used = Number.isFinite(usage) && (usage ?? 0) >= 0 ? (usage ?? 0) : 0;
  const remaining =
    remainingProp != null && Number.isFinite(remainingProp)
      ? Math.max(0, remainingProp)
      : Math.max(0, total - used);

  return (
    <span
      className={cn(
        'tabular-nums',
        compact ? 'text-[10px] text-muted-foreground' : 'text-xs text-muted-foreground',
        className,
      )}
      title={`${remaining.toLocaleString()} left of ${total.toLocaleString()} total`}
    >
      {remaining.toLocaleString()} left
    </span>
  );
};
